
import { CardOptions, CreditCardElements as CardElements, DigitalWallets, DigitalWalletsOptions } from '@olo/pay';
import { Component, InjectReactive, Vue } from 'vue-property-decorator';
import { Getter, Mutation } from 'vuex-class';

import ApplyPromotions from '@/components/ApplyPromotions.vue';
import ApplyTip from '@/components/ApplyTip.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import OrderLocationDetails from '@/components/OrderLocationDetails.vue';
import OrderTotals from '@/components/OrderTotals.vue';
import ProductsSummary from '@/components/ProductsSummary.vue';
import { BillingMethod, MutationTypes, OrderMethods, PaymentMethods } from '@/enums';
import BasketModule from '@/store/modules/basket';
import UserModule from '@/store/modules/user';
import { submitPurchase } from '@/utils/events.utils';
import { formatPhoneNumber, parseDate } from '@/utils/global.util';

const { SET_EDIT_IS_OPEN } = MutationTypes;

@Component({
  components: {
    ApplyPromotions,
    ApplyTip,
    OrderLocationDetails,
    OrderTotals,
    ProductsSummary
  },
  metaInfo: {
    title: 'Cousins Subs',
    titleTemplate: '%s - Order - Checkout'
  }
})
export default class Checkout extends Vue {
  @InjectReactive('progressRef') progressRef: any;
  @InjectReactive() isMobile!: boolean;
  @Getter activeRestaurant: Restaurant;
  @Getter editIsOpen: boolean;
  @Mutation(SET_EDIT_IS_OPEN) setEditIsOpen: (action: boolean) => void;

  selectedPaymentMethod: string;
  showNewPaymentForm: boolean;
  showCreditCardForm: boolean;
  showGiftCardForm: boolean;
  showDigitalWalletForm: boolean;
  showSplitPayForm: boolean;
  isActive1: boolean;
  isActive2: boolean;
  isActive3: boolean;
  digitalToken: string;
  digitalWalletObj: {};
  walletType: number;

  formModel: {
    firstname: string;
    lastname: string;
    emailaddress: string;
    contactnumber: string;
    cardnumber: string;
    expirymonth: number;
    expiryyear: number;
    cvv: string;
    zip: number;
    savecreditcardonfile: boolean;
    giftnumber: string;
    giftpin: string;
    savegiftcardonfile: boolean;
    cardtype: string;
    cardlastfour: string;
    token: string;
    billingschemeid: number;

  };

  // Digital Wallet
  wallets = new DigitalWallets('production');

  // Credit Card Element
  cardElements = new CardElements('production');
  crediCardStyleObj = {
    base: {
      color: 'rgba(100,97,97,var(--tw-text-opacity))',
      fontSize: '2.25rem',
      lineHeight: '2.5rem',
      padding: '0.5rem',
      fontFamily: 'TradeGothicW01-BoldCn20_675334,Oswald TT Semibold,Arial Narrow,Arial-serif',
      backgroundColor: '#EAEAEA',
    },
  };

  createCardElements = async () => {
    const options: CardOptions = {
      cardElementOptions: {
        style: this.crediCardStyleObj,
      },
      mountTargets: {
        number: '#cardnumber',
        expiry: '#cardexpiry',
        cvc: '#cardcvc',
      },
      placeholders: {
        cvc: '',
        expiry: '',
        number: '',
      },
    };
    await this.cardElements.create(options);
  };

  async mounted() {
    this.showSplitPayForm = false;
    this.isActive1 = true;
    this.isActive2 = false;
    this.isActive3 = false;
    await this.createCardElements();
    this.$scrollTo(this.progressRef as Element);
    try {
      if (this.currentBasket.timeMode === 'advance') {
        if (this.currentBasket.timeWanted) {
          const timeWanted = parseDate(this.currentBasket.timeWanted);
          const now = new Date(Date.now());
          if (timeWanted < now) {
            this.setEditIsOpen(true);
          }
        } else {
          this.setEditIsOpen(true);
        }
      }

      if (this.currentBasket.deliveryMode === OrderMethods.CURBSIDE) {
        if (!BasketModule.curbsideDetailsExist) {
          this.setEditIsOpen(true);
          this.$toasted.error('Please enter your vehicle details.');
        }
      }

      if (BasketModule.id) {
        await BasketModule.validateBasket({ basketId: BasketModule.id });
      }
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err.data);
      if (err.data.includes('Please choose another available time.')) {
        this.setEditIsOpen(true);
      }
    }
  }

  async created() {
    if (BasketModule.id) {
      if (UserModule.loggedIn) {
        await UserModule.getBillingAccounts(BasketModule.id);
        if (UserModule.savedPaymentDetails) {
          const defaultPaymentMethod = UserModule.savedPaymentDetails.find((value) => value.isDefault);
          if (defaultPaymentMethod) {
            this.selectedPaymentMethod = defaultPaymentMethod.accountId.toString();
          } else if (UserModule.savedPaymentDetails && UserModule.savedPaymentDetails.length > 0) {
            this.selectedPaymentMethod = UserModule.savedPaymentDetails[0]?.accountId?.toString();
          }
        }
      }
    } else {
      this.$router.push(
        {
          name: 'SelectLocation',
          params: {
            ...this.$route.params,
            location: this.activeRestaurant.slug,
          }
        });
    }
  }

  data() {
    return {
      formModel: {
        firstname: '',
        lastname: '',
        emailaddress: '',
        contactnumber: '',
        cardnumber: '',
        expirymonth: null,
        expiryyear: null,
        cvv: '',
        zip: '',
        savecreditcardonfile: false,
        giftcardnumber: '',
        giftcardpin: '',
        savegiftcardonfile: false,
        cardtype: '',
        cardlastfour: '',
        token: '',
        billingschemeid: null
      },
      selectedPaymentMethod: null,
      showNewPaymentForm: false,
      showCreditCardForm: true,
      showGiftCardForm: false,
      showDigitalWalletForm: false,
      showSplitPayForm: false,
      digitalToken: '',

    }
  }

  async submitHandler(submitResult) {
    const loader = this.$loading.show();
    if (this.showDigitalWalletForm) {
      this.digitalWalletObj = JSON.parse(this.digitalToken);
    }
    try {
      const hasErrors = await submitResult.hasValidationErrors();
      if (hasErrors) {
        this.scrollToError(submitResult.form.$el);
        if (!this.formModel.token && !this.formModel.giftnumber && !this.formModel.billingschemeid && !this.selectedPaymentMethod) {
          this.$modal.show(
            ErrorModal, {
              message: 'Please provide a payment method.',
            }, {
              clickToClose: false,
              scrollable: true,
              focusTrap: true,
              height: 'auto',
              width: this.isMobile ? '90%' : '60%',
            }
          );
        }
      } else {
        const data = await submitResult.values();
        const {
          firstname,
          lastname,
          emailaddress,
          contactnumber,
          cardnumber,
          expiryyear,
          expirymonth,
          cvv,
          zip,
          savecreditcardonfile,
          giftcardnumber,
          giftcardpin,
          savegiftcardonfile,
          cardtype,
          cardlastfour,
          token,
          billingschemeid
        } = data;
        let submitRequest = {} as SubmitBasketRequest;
        let submitRequestMultiPayment = {} as SubmitBasketRequestMultiPayment;
        if ((this.showNewPaymentForm || !this.availablePaymentMethods) && this.showSplitPayForm) {
          const creditCardResponse = (await this.cardElements.createPaymentMethod());
          submitRequestMultiPayment = {
            billingAccounts: [
              {
                token: creditCardResponse?.paymentMethod?.id,
                billingmethod: BillingMethod.creditcardtoken,
                amount: 0,
                tipportion: 0,
                expiryyear: creditCardResponse?.paymentMethod?.card?.exp_year,
                expirymonth: creditCardResponse?.paymentMethod?.card?.exp_month,
                zip,
                cardtype: creditCardResponse?.paymentMethod?.card?.brand,
                cardlastfour: creditCardResponse?.paymentMethod?.card?.last4,
              },
              {
                amount: this.currentBasket.total,
                tipportion: 0,
                billingmethod: BillingMethod.storedvalue,
                billingfields: [
                  {
                    name: 'number',
                    value: giftcardnumber
                  },
                  {
                    name: 'pin',
                    value: giftcardpin
                  }
                ]
              }
            ]
          }
        } else if ((this.showNewPaymentForm || !this.availablePaymentMethods) && this.showGiftCardForm) {
          submitRequest = {
            billingmethod: BillingMethod.storedvalue,
            billingfields: [
              {
                name: 'number',
                value: giftcardnumber
              },
              {
                name: 'pin',
                value: giftcardpin
              }
            ],
            saveonfile: savegiftcardonfile
          }
        } else if ((!this.showNewPaymentForm && UserModule.savedPaymentDetails && UserModule.savedPaymentDetails.length > 0) && this.isLoggedIn && this.selectedPaymentMethod) {
          const billingAccount = UserModule.savedPaymentDetails.find((paymentMethod) => paymentMethod.accountId === parseInt(this.selectedPaymentMethod));
          submitRequest = {
            billingmethod: BillingMethod.billingaccount,
            billingaccountid: billingAccount?.accountId
          }
        } else {
          if (this.showCreditCardForm) {
            const creditCardResponse = (await this.cardElements.createPaymentMethod());
            submitRequest = {
              token: creditCardResponse?.paymentMethod?.id,
              billingmethod: BillingMethod.creditcardtoken,
              expiryyear: creditCardResponse?.paymentMethod?.card?.exp_year,
              expirymonth: creditCardResponse?.paymentMethod?.card?.exp_month,
              zip,
              cardtype: creditCardResponse?.paymentMethod?.card?.brand,
              cardlastfour: creditCardResponse?.paymentMethod?.card?.last4,
              saveonfile: savecreditcardonfile
            };
          }
          if (this.showDigitalWalletForm) {
            const digiObj = JSON.parse(this.digitalToken);

            if (digiObj.card.wallet.type.includes('google')) {
              this.walletType = 1;
            }
            if (digiObj.card.wallet.type.includes('apple')) {
              this.walletType = 2;
            }
            submitRequest = {
              token: digiObj.id,
              billingmethod: BillingMethod.digitalwallet,
              cardtype: digiObj.card.brand,
              cardlastfour: digiObj.card.last4,
              billingschemeid: this.walletType,

            };
          }
        }
        if (!this.isLoggedIn) {
          if (this.showSplitPayForm) {
            submitRequestMultiPayment = {
              ...submitRequestMultiPayment,
              firstname,
              lastname,
              emailaddress,
              contactnumber
            }
          }
          submitRequest = {
            ...submitRequest,
            firstname,
            lastname,
            emailaddress,
            contactnumber
          }
        }

        try {
          BasketModule.setCustomerFirstName(firstname);
          try {
            const basketTotal = this.currentBasket.total;
            const productIds = this.currentBasket.products.map((value) => value.productId);
            submitPurchase(basketTotal, productIds);
          } catch (err) {
            this.$log.error(err);
          }
          this.$gtag.purchase({
            transaction_id: this.currentBasket.id.valueOf(),
            affiliation: 'Cousins subs',
            value: this.currentBasket.total.valueOf(),
          });
          if (this.showSplitPayForm) {
            await BasketModule.placeOrderMultiPayment({ basketId: BasketModule.id, submitRequestMultiPayment });
          } else {
            await BasketModule.placeOrder({ basketId: BasketModule.id, submitRequest });
          }
          this.$router.push(
            {
              name: 'OrderConfirmation',
              params: {
                ...this.$route.params
              }
            });
          this.$toasted.success('Your order was successfully placed!');
        } catch (err) {
          this.$log.error(err);
          this.$toasted.error(err.data);
        }
      }
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err.data);
    } finally {
      loader.hide();
    }
  }

  // Digital wallets

  digitalWallets: {};
  paymentMethod: null;
  onPaymentSubmit(paymentMethod, wallets) {
    if (paymentMethod) {
      this.paymentMethod = paymentMethod !== null ? paymentMethod : null;
      try {
        setTimeout(() => {
          if (paymentMethod) {
            const ObjSerial = JSON.stringify(paymentMethod);
            this.digitalToken = ObjSerial;
          }

          document.getElementById('placeOrder')?.click();

          if (wallets) {
            wallets.completePaymentEvent();
          }
        }, 5000);
      } catch (e) {
        this.$log.error(e);
        if (wallets) {
          wallets.failPaymentEvent();
        }
      }
    }
  };

  initializeWallets = async () => {
    const digitalWalletsOptions: DigitalWalletsOptions = {
      options: {
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Cousins Subs',
          amount: parseInt(this.currentBasket.total.toString().replace('.', '')),
        },
      },
      style: {
        type: 'default', // other types: "book" | "buy" | "donate"
        theme: 'dark', // other themes: "light" | "light-outline"
        height: '80px'
      }
    };
    const canRenderButton = await this.wallets?.canRenderButton();
    if (canRenderButton) {
      await this.wallets.initialize(digitalWalletsOptions);
      this.wallets.mountButton((paymentMethod) => this.onPaymentSubmit(paymentMethod, this.wallets));
      this.digitalWallets = this.wallets;
    }
  };

  scrollToError($formEl) {
    const errorWrapperEl = $formEl.querySelector('[data-has-errors="true"]');
    const errorWrapperInput = errorWrapperEl && errorWrapperEl.querySelector('input,select,textarea');
    if (errorWrapperInput) {
      errorWrapperInput.focus();
      errorWrapperInput.scrollIntoView({ block: 'center' });
    }
  }

  handleUpdateItemsClick() {
    this.$router.push(
      {
        name: 'OrderSummary',
        params: {
          ...this.$route.params
        }
      });
  }

  get isLoggedIn() {
    return UserModule.loggedIn;
  }

  get customerFullName() {
    return UserModule.fullName;
  }

  get customerEmail() {
    return UserModule.email;
  }

  get customerPhoneNumber() {
    return UserModule.phoneNumber;
  }

  get paymentMethods() {
    return Object.values(PaymentMethods)?.map(({ text, value }) => ({ id: text.split(' ').join('-'), value, label: text })) || [];
  }

  get currentYear() {
    return new Date().getFullYear();
  }

  get expirationYears() {
    const years: number[] = [];
    const totalYears = 20;
    for (let index = 0; index < totalYears; index++) {
      const year = this.currentYear + index;
      years.push(year);
    }
    return years;
  }

  get isTipAllowed() {
    return BasketModule.current?.allowsTip || false;
  }

  get currentBasketProducts() {
    return BasketModule.current?.products;
  }

  get currentBasket() {
    return BasketModule.current || {};
  }

  get availablePaymentMethods() {
    if (UserModule.savedPaymentDetails && UserModule.savedPaymentDetails.length > 0) {
      return UserModule.savedPaymentDetails.reduce((acc: any, { description, accountId, accountType }: BillingAccount) => {
        if (accountType === 'Cousins Subs Gift Card' && !this.supportsGiftCardPayment) {
          return acc;
        }
        acc.push({ label: description, value: accountId });
        return acc;
      }, []) || null;
    } else {
      return null;
    }
  }

  get supportsGiftCardPayment() {
    let supports = false;

    if (this.activeRestaurant.name === 'Cousins Brink Demo Lab (1000)') {
      supports = this.activeRestaurant.billingMethods?.includes('Cousins Subs Gift Card') || this.activeRestaurant.billingMethods?.includes('Generic Mock Gift Card') || false;
    } else {
      supports = this.activeRestaurant.billingMethods?.includes('Cousins Subs Gift Card') || false;
    }

    return supports;
  }

  get readyTime() {
    return (this.currentBasket.timeMode === 'advance' ? this.currentBasket.timeWanted : this.currentBasket.earliestReadyTime)
  }

  handleShowAddPaymentMethodClick() {
    this.createCardElements();
    this.showNewPaymentForm = true;
  }

  handleCancelAddPaymentMethodClick() {
    this.showNewPaymentForm = false;
    if (this.wallets) {
      this.wallets.unmount();
    }
  }

  handleShowCreditCardClick() {
    this.createCardElements();
    this.showCreditCardForm = true;
    this.showGiftCardForm = false;
    this.showDigitalWalletForm = false;
    this.isActive1 = true;
    this.isActive2 = false;
    this.isActive3 = false;
  }

  handleShowGiftCardClick() {
    this.showGiftCardForm = true;
    this.showCreditCardForm = false;
    this.showDigitalWalletForm = false;
    this.isActive1 = false;
    this.isActive2 = true;
    this.isActive3 = false;
  }

  handleShowDigitalWalletClick() {
    this.initializeWallets();
    this.showGiftCardForm = false;
    this.showCreditCardForm = false;
    this.showDigitalWalletForm = true;
    this.isActive1 = false;
    this.isActive2 = false;
    this.isActive3 = true;
  }

  formatPhoneNumber(phoneNumberString) {
    return formatPhoneNumber(phoneNumberString);
  }
}
